import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useInstituteAuth } from "../../context/InstituteAuthContext";
import { toast } from "react-toastify";
import HeaderMain from "../../dasboard-component/Components/HeaderMain";

const InstituteGameSelection = () => {
  const [arenas, setArenas] = useState([]);
  const [selectedArena, setSelectedArena] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { instituteUser } = useInstituteAuth();
  const instituteId = instituteUser?.id;

  // Date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Fetch institute's arenas
  useEffect(() => {
    if (instituteId) {
      fetchInstituteArenas();
    }
  }, [instituteId]);

  const fetchInstituteArenas = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/arenas`,
        { withCredentials: true }
      );
      // Filter only enabled arenas
      const enabledArenas = response.data.filter((arena) => arena.is_enabled);
      setArenas(enabledArenas);
    } catch (error) {
      console.error("Error fetching arenas:", error);
      toast.error("Failed to fetch arenas");
    }
  };

  // Fetch facilities when arena is selected
  useEffect(() => {
    if (selectedArena) {
      fetchFacilities(selectedArena);
    }
  }, [selectedArena]);

  const fetchFacilities = async (arenaId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/institute/arena-facilities?arenaId=${arenaId}`,
        { withCredentials: true }
      );
      setFacilities(response.data);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      toast.error("Failed to fetch facilities");
    }
  };

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedArena && selectedFacility && selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedArena, selectedFacility, selectedDate]);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/institute/available-slots`,
        {
          params: {
            arenaId: selectedArena,
            facilityId: selectedFacility,
            date: selectedDate,
          },
          withCredentials: true,
        }
      );
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching available slots:", error);
      toast.error("Failed to fetch available slots");
    }
  };

  const handleProceed = async () => {
    if (!selectedArena || !selectedFacility || !selectedDate || !selectedSlot) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);

    try {
      // Create booking for the institute
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/institute/create-booking`,
        {
          arenaId: selectedArena,
          facilityId: selectedFacility,
          date: selectedDate,
          slotId: selectedSlot,
          instituteId: instituteId,
        },
        { withCredentials: true }
      );

      const { bookingId, sport } = response.data;

      // Get arena and facility details for navigation
      const arena = arenas.find((a) => a.id === selectedArena);
      const facility = facilities.find((f) => f.id === selectedFacility);
      const slot = availableSlots.find((s) => s.slot_id === selectedSlot);

      // Prepare data for scorecard page
      const data = {
        sport: sport,
        arenaBooking: {
          id: bookingId,
          name: arena?.Arena_name || "Arena",
          date: formatDate(selectedDate),
          time: slot?.time || "",
        },
        invitedUsers: [], // No invited users for institute bookings
        isInstitute: true, // Flag to indicate institute context
        instituteId: instituteId,
      };

      // Navigate to appropriate sport page
      if (sport === "Snooker") {
        navigate("/add-snooker", { state: data });
      } else if (sport === "Tennis") {
        navigate("/add-tennis", { state: data });
      } else if (sport === "Table tennis") {
        navigate("/add-table-tennis", { state: data });
      } else if (sport === "Golf") {
        navigate("/add-golf", { state: data });
      } else if (sport === "Football") {
        navigate("/add-football", { state: data });
      } else if (sport === "Basketball") {
        navigate("/add-basketball", { state: data });
      } else if (sport === "Padel Tennis") {
        navigate("/add-padel-tennis", { state: data });
      } else if (sport === "Volleyball") {
        navigate("/add-volleyball", { state: data });
      } else if (sport === "Cricket") {
        navigate("/add-cricket", { state: data });
      } else if (sport === "Hockey") {
        navigate("/add-hockey", { state: data });
      } else if (sport === "Swimming") {
        navigate("/add-swimming", { state: data });
      } else if (sport === "Squash") {
        navigate("/add-squash", { state: data });
      } else {
        toast.error("This sport is not supported yet!");
      }
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(
        error.response?.data?.message || "Failed to create booking"
      );
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      maxWidth: "960px",
      width: "100%",
      margin: "24px auto",
      padding: "24px 16px",
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      boxSizing: "border-box",
    },
    sectionTitle: {
      fontSize: "clamp(20px, 4vw, 24px)",
      fontWeight: "600",
      color: "#2c3e50",
      marginBottom: "15px",
      textAlign: "center",
      position: "relative",
      paddingBottom: "10px",
    },
    titleUnderline: {
      content: "''",
      position: "absolute",
      bottom: "0",
      left: "50%",
      transform: "translateX(-50%)",
      width: "80px",
      height: "3px",
      backgroundColor: "#3498db",
      borderRadius: "2px",
    },
    selectionStep: {
      backgroundColor: "#f8fafc",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "18px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      border: "1px solid #e2e8f0",
    },
    stepNumber: {
      display: "inline-block",
      width: "30px",
      height: "30px",
      backgroundColor: "#3498db",
      color: "white",
      borderRadius: "50%",
      textAlign: "center",
      lineHeight: "30px",
      marginRight: "10px",
      fontWeight: "bold",
    },
    stepTitle: {
      display: "inline-block",
      fontSize: "clamp(16px, 3.8vw, 20px)",
      fontWeight: "600",
      color: "#2c3e50",
      marginBottom: "15px",
    },
    selectWrapper: {
      marginBottom: "16px",
    },
    select: {
      width: "100%",
      padding: "12px",
      fontSize: "16px",
      borderRadius: "8px",
      border: "1px solid #cbd5e0",
      backgroundColor: "white",
      cursor: "pointer",
    },
    input: {
      width: "100%",
      padding: "12px",
      fontSize: "16px",
      borderRadius: "8px",
      border: "1px solid #cbd5e0",
      backgroundColor: "white",
      boxSizing: "border-box",
    },
    proceedButton: {
      marginTop: "24px",
      padding: "12px 30px",
      fontSize: "16px",
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontWeight: "600",
      boxShadow: "0 4px 6px rgba(52, 152, 219, 0.3)",
      transition: "all 0.3s ease",
      width: "100%",
      boxSizing: "border-box",
    },
    errorMessage: {
      color: "#e74c3c",
      fontWeight: "500",
      textAlign: "center",
      padding: "10px",
      backgroundColor: "#fdf2f2",
      borderRadius: "8px",
      marginTop: "10px",
      border: "1px solid #fed7d7",
    },
  };

  return (
    <div>
      <HeaderMain pageType="InstituteAddGame" />

      <div style={styles.container}>
        <h1 style={styles.sectionTitle}>
          Add Game Scorecard
          <div style={styles.titleUnderline}></div>
        </h1>

        {/* Step 1: Select Arena */}
        <div style={styles.selectionStep}>
          <div>
            <span style={styles.stepNumber}>1</span>
            <span style={styles.stepTitle}>Select Arena</span>
          </div>
          <div style={styles.selectWrapper}>
            <select
              style={styles.select}
              value={selectedArena || ""}
              onChange={(e) => {
                setSelectedArena(Number(e.target.value));
                setSelectedFacility(null);
                setSelectedDate("");
                setSelectedSlot(null);
              }}
            >
              <option value="">Choose an arena...</option>
              {arenas.map((arena) => (
                <option key={arena.id} value={arena.id}>
                  {arena.Arena_name}
                </option>
              ))}
            </select>
            {arenas.length === 0 && (
              <p style={styles.errorMessage}>
                No arenas found. Please add an arena first.
              </p>
            )}
          </div>
        </div>

        {/* Step 2: Select Facility */}
        {selectedArena && (
          <div style={styles.selectionStep}>
            <div>
              <span style={styles.stepNumber}>2</span>
              <span style={styles.stepTitle}>Select Facility (Sport)</span>
            </div>
            <div style={styles.selectWrapper}>
              <select
                style={styles.select}
                value={selectedFacility || ""}
                onChange={(e) => {
                  setSelectedFacility(Number(e.target.value));
                  setSelectedDate("");
                  setSelectedSlot(null);
                }}
              >
                <option value="">Choose a facility...</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.sport_name} - {facility.about_facility}
                  </option>
                ))}
              </select>
              {facilities.length === 0 && (
                <p style={styles.errorMessage}>
                  No facilities found for this arena.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Select Date */}
        {selectedFacility && (
          <div style={styles.selectionStep}>
            <div>
              <span style={styles.stepNumber}>3</span>
              <span style={styles.stepTitle}>Select Date</span>
            </div>
            <div style={styles.selectWrapper}>
              <input
                type="date"
                style={styles.input}
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedSlot(null);
                }}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
          </div>
        )}

        {/* Step 4: Select Slot */}
        {selectedDate && (
          <div style={styles.selectionStep}>
            <div>
              <span style={styles.stepNumber}>4</span>
              <span style={styles.stepTitle}>Select Time Slot</span>
            </div>
            <div style={styles.selectWrapper}>
              <select
                style={styles.select}
                value={selectedSlot || ""}
                onChange={(e) => setSelectedSlot(Number(e.target.value))}
              >
                <option value="">Choose a time slot...</option>
                {availableSlots.map((slot) => (
                  <option key={slot.slot_id} value={slot.slot_id}>
                    {slot.time}
                  </option>
                ))}
              </select>
              {availableSlots.length === 0 && selectedDate && (
                <p style={styles.errorMessage}>
                  No available slots for this date. Please enable the slot in the arena settings.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Proceed Button */}
        {selectedSlot && (
          <div style={{ textAlign: "center" }}>
            <button
              style={styles.proceedButton}
              onClick={handleProceed}
              disabled={loading}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#2980b9";
                e.currentTarget.style.boxShadow =
                  "0 6px 8px rgba(52, 152, 219, 0.4)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#3498db";
                e.currentTarget.style.boxShadow =
                  "0 4px 6px rgba(52, 152, 219, 0.3)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {loading ? "Creating Booking..." : "Proceed to Game Setup"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstituteGameSelection;

